import React, { useEffect, useState } from 'react';
import { useForm, get, FieldValues } from 'react-hook-form';
import { saveAs } from 'file-saver';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { exportXlsxAction, getExportLists } from '../../actions/export';
import { getAllStructuresAction } from '../../actions/structures';
import { ErrorField, StyledSelect } from '../../lib/HooksFormFields';
import { IStructure } from '../../types/structure';
import styles from './ExportAdmin.module.scss';
import { validateExportYears } from '../../utils';
import useExportYearPeriod from '../../hooks/useExportYearPeriod';

const exportTypes = [
  {
    value: 'annualization',
    label: 'Annualisation',
  },
  {
    value: 'common-pay-elements',
    label: 'Elements de paie commun',
  },
  {
    value: 'monthly-hours',
    label: 'Suivi heures',
  },
];

const ExportAdmin = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const userAuth = useSelector((store: any) => store.authReducer)?.user;
  const { structuresList } = useSelector((store: any) => store.structureReducer);
  const { exportList } = useSelector((store: any) => store.shiftsReducer);

  const {
    control,
    reset,
    setValue,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<FieldValues>();

  const form = watch();
  const structuresOptions = structuresList?.map(
    (s: IStructure) => ({ value: s._id, label: s.name }),
  );

  const { options } = useExportYearPeriod({
    watch,
    setValue,
    exportList,
  });

  async function onSubmit(values: FieldValues) {
    setIsLoading(true);
    setApiError(null);
    const startDate = format(new Date(values.startDate), 'yyyy-MM-dd');
    const endDate = format(new Date(values.endDate), 'yyyy-MM-dd');
    const data = {
      type: values.type,
      startDate,
      endDate,
      structure: values.structure,
    };
    try {
      const res : any = await exportXlsxAction(dispatch, data);

      if (res) {
        const blob = new Blob([res]);
        const type = exportTypes?.find((t: any) => t.value === values.type)?.label;
        const structure = structuresOptions?.find((t: any) => t.value === values.structure)?.label;
        const filename = `${type}-${structure}-${startDate}-${endDate}.xlsx`;
        saveAs(blob, filename);
      }
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setApiError('Une erreur c\'est produite lors de l\'export');
    }
  }

  useEffect(() => {
    getAllStructuresAction(dispatch);
    getExportLists(dispatch);
  }, []);

  useEffect(() => {
    const currentYear = exportList?.years?.find((y: { isCurrent: boolean }) => y.isCurrent);
    if (currentYear) {
      reset({
        type: 'annualization',
        structure: localStorage.getItem('structure'),
        years: [currentYear.value],
      });
    }
  }, [exportList?.years]);

  return (
    <div className={styles.modale}>
      <div className={styles.types}>
        {exportTypes.map((t) => (
          <button
            key={t.value}
            type='button'
            className={form?.type === t.value ? styles.active : ''}
            onClick={() => setValue('type', t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {typeof get(errors, 'type')?.message === 'string' && (
        <ErrorField message={get(errors, 'type')?.message || ''}/>
      )}
      <div className={styles.form}>
        <div className={styles.row}>
          {(userAuth.role === 'Admin' || userAuth.role === 'Owner')
            && <div className={styles['container-field']}>
              <StyledSelect
                  name="structure"
                  label="Structure"
                  control={control}
                  options={structuresOptions}
                  rules={{
                    required: 'la structure est requise',
                  }}
              />
              {typeof get(errors, 'structure')?.message === 'string' && (
                <ErrorField message={get(errors, 'structure')?.message || ''}/>
              )}
            </div>
          }
        <div className={styles['container-field']}>
          <StyledSelect
            name="years"
            label="Année(s)"
            control={control}
            isMulti
            maxSelections={2}
            options={exportList?.years}
            rules={{
              validate: (value: number[] | undefined) => validateExportYears(value),
            }}
          />
          {typeof get(errors, 'years')?.message === 'string' && (
            <ErrorField message={get(errors, 'years')?.message || ''}/>
          )}
        </div>
        </div>
      {form.years?.length > 0
        && <div className={styles.row}>
          <div className={styles['container-field']}>
            <StyledSelect
                name="startDate"
                label={'Mois de départ'}
                control={control}
                options={options.startDate}
                rules={{
                  required: 'le mois de départ est requis',
                }}
            />
            {typeof get(errors, 'startDate')?.message === 'string' && (
              <ErrorField message={get(errors, 'startDate')?.message || ''}/>
            )}
          </div>
          <div className={styles['container-field']}>
            <StyledSelect
                name="endDate"
                label={'Mois de fin'}
                control={control}
                options={options.endDate}
                rules={{
                  required: 'le mois de fin est requis',
                }}
            />
            {typeof get(errors, 'endDate')?.message === 'string' && (
              <ErrorField message={get(errors, 'endDate')?.message || ''}/>
            )}
          </div>
        </div>
      }
      {apiError && (
        <div className={styles.error}>
          <ErrorField message={apiError}/>
        </div>
      )}
      {!isLoading
        ? <div className={styles.actions}>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
          >
            Exporter
          </button>
        </div>
        : <div className={styles.actions}>
          Export en cours...
        </div>
      }
    </div>
    </div>
  );
};

export default ExportAdmin;
